<template>
    <div class="playbook-creation-track h-100 bg-stone-900" :class="[{ hide: hideStepTracker }]">
        <div class="headings-holder bg-moat-800">
            <h5 v-if="heading" class="heading text-on-dark p-xl">
                {{ heading }}
            </h5>
            <p
                v-if="headingDesc"
                id="headingDesc"
                class="delay-hook heading-desc text-stone-800 mb-xl"
            >
                {{ headingDesc }}
            </p>
        </div>
        <div class="track-holder bg-stone-900 p-xl">
            <div class="init-step-container">
                <div
                    v-for="(step, index) in playbookGenerationSteps"
                    class="step pb-xl"
                    :class="[{ current: step.isCurrentStep }, { complete: step.isCompletedStep }]"
                >
                    <div class="wrap-helper display-flex align-items-center">
                        <div class="path-holder">
                            <div class="path"></div>
                        </div>
                        <div class="step-num display-flex justify-center align-items-center">
                            <p v-if="!step.isCompletedStep" class="text-on-dark">{{ index + 1 }}</p>
                            <font-awesome-icon
                                v-else
                                icon="fas fa-check"
                                class="text-on-dark"
                                size="lg"
                            />
                        </div>
                        <p
                            class="large bold step-label text-on-dark ml-sm"
                            :class="{ 'redacted-regular': step.redacted }"
                        >
                            {{ step.stepName }}
                        </p>
                        <font-awesome-icon
                            v-if="playbookGenerationStore.shouldUpdateContenderList && index === 3"
                            icon="fas fa-circle-exclamation"
                            class="text-moat-600 ml-md"
                            size="lg"
                            v-tooltip="{
                                content:
                                    'We\'ve noticed a change in the selected Technology Areas. Updating Contenders...',
                            }"
                        />
                    </div>
                    <p
                        class="text-on-dark step-desc pl-11 mt-sm"
                        :class="{ 'redacted-regular': step.redacted }"
                    >
                        {{ step.stepDesc }}
                    </p>
                    <div
                        v-if="
                            playbookGenerationStore.trackStepsFinal.length !== 0 &&
                            playbookGenerationStore.playbookSelections[index]?.value &&
                            playbookGenerationStore.trackStepsFinal[index].isCompletedStep
                        "
                        class="step-selection mt-md ml-lg px-md display-flex align-items-center justify-center"
                        :class="{
                            'allow-edit':
                                playbookGenerationStore.playbookSelections[index].allowEdit,
                        }"
                        @click="
                            gotoStep(
                                playbookGenerationStore.playbookSelections[index].allowEdit,
                                step.stepNum
                            )
                        "
                    >
                        <p>
                            {{
                                Array.isArray(
                                    playbookGenerationStore.playbookSelections[index].value
                                )
                                    ? `${playbookGenerationStore.playbookSelections[index].value.length}  ${playbookGenerationStore.playbookSelections[index].selectionLabel}`
                                    : playbookGenerationStore.playbookSelections[index]
                                          .selectionLabel
                            }}
                        </p>
                        <font-awesome-icon
                            v-if="!playbookGenerationStore.playbookSelections[index].allowEdit"
                            icon="fas fa-lock"
                            class="text-stone-800 ml-md"
                            size="lg"
                        />
                        <font-awesome-icon
                            v-else
                            icon="fas fa-gear"
                            class="text-moat-600 ml-md"
                            size="lg"
                        />
                    </div>
                </div>
                <div
                    v-if="playbookGenerationStore.trackStepsFinal.length === 0"
                    class="cover-up"
                ></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { usePlaybookGenerationStore } from '@/stores'

const playbookGenerationStore = usePlaybookGenerationStore()
const emit = defineEmits(['step-clicked'])

const props = defineProps({
    passedSteps: {
        type: Object,
        default: () => {},
    },
    currentStep: {
        type: Number,
        default: null,
    },
    heading: {
        type: String,
        default: '',
    },
    headingDesc: {
        type: String,
        default: '',
    },
    hideOnStep: {
        type: Number,
        default: null,
    },
    allowStepNavigation: {
        type: Boolean,
        default: false,
    },
})

const collapse = ref(false)
const headingsHolderHeight = ref()

onMounted(() => {})

const playbookGenerationSteps = computed(() => {
    return playbookGenerationStore.trackStepsFinal.length > 0
        ? playbookGenerationStore.trackStepsFinal
        : playbookGenerationStore.trackStepsInit
})

const gotoStep = (allowEdit, stepNum) => {
    if (!allowEdit) {
        return
    }
    playbookGenerationStore.setCurrentStep(stepNum)
}

const hideStepTracker = computed(() => {
    return props.hideOnStep && props.currentStep ? props.currentStep === props.hideOnStep : false
})
</script>

<style lang="scss" scoped>
.playbook-creation-track {
    width: 100%;
    min-width: 400px;
    max-width: 400px;
    height: calc(100vh - 142px);
    border-right: 1px solid var(--color-stone-200);
    background: var(--color-moat-80);
    overflow: auto;

    position: relative;

    transition: all 0.3s ease-in-out;

    .init-step-container {
        position: relative;
        .cover-up {
            height: 100%;
            width: 100%;
            background: linear-gradient(0deg, rgba(34, 39, 50, 1) 15%, rgba(34, 39, 50, 0) 100%);

            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
        }
    }

    .step {
        position: relative;

        // With Current Class applied
        &.current {
            .step-num {
                border-color: var(--color-gold) !important;

                p {
                    color: var(--color-gold) !important;
                }
            }

            .step-label {
                color: var(--color-gold) !important;
            }

            .complete-path {
                top: calc(50% - 9px);
            }

            .path-holder {
                .path {
                    &:before {
                        opacity: 1;
                    }
                }
            }
        }

        &.complete {
            .path-holder {
                .path {
                    border-style: solid;
                }
            }
        }

        &:last-of-type {
            .path-holder {
                .path {
                    pointer-events: none;
                    opacity: 0;
                }
            }
        }

        // Base State
        .step-num {
            width: 36px;
            height: 36px;
            border: solid 2px white;
            border-radius: 50%;
            background: var(--color-moat-80);

            position: relative;
            z-index: 1;
        }

        .path-holder {
            height: calc(100% - 36px);

            position: absolute;
            top: 36px;
            left: 17px;
            z-index: 0;

            .path {
                height: calc(100%);
                border-right: dashed 2px white;

                position: relative;

                &:before {
                    content: '';
                    display: block;
                    width: 2px;
                    height: 50%;
                    background: var(--color-gold);

                    position: absolute;
                    top: 0;
                    left: 0;

                    opacity: 0;
                }
            }
        }

        .step-selection {
            width: fit-content;
            max-width: 350px;
            height: 50px;
            background: white;
            border-radius: 0 25px 25px 0;
            outline: 1px solid var(--color-moat-700);
            border: 2px solid var(--color-moat-700);

            &.allow-edit {
                cursor: pointer;
                p {
                    color: var(--color-moat-700);
                    text-decoration: underline;
                }
            }
        }
    }

    &.hide {
        display: none;
    }

    .handle {
        height: 40px;
        width: 40px;
        border-radius: 50%;
        cursor: pointer;

        position: absolute;
        top: 50%;
        right: -20px;
        background: white;
        transform: translateY(-50%);

        transition: all 0.3s ease-in-out;

        .icon {
            transform: rotate(0);

            transition: all 0.3s ease-in-out;
        }
    }
}
</style>
